import * as THREE from "three";
import { entityDefinitions } from "../../data/entities";
import { FACE, G, Kit, hdr, instance, once } from "./kit";
import { blobShadow, decalMaterial, glowSprite, printMaterial } from "./fx";
import { DISPLAY_FONT, canvasTex, roundRectPath, stripesTex } from "./tex";

/**
 * Road barriers, all facing −z (the oncoming camera), origin = lane centre on the road, footprint from
 * entityDefinitions. Lamps on top blink in `animateProp` (children named "lamp…").
 */

const LOW = entityDefinitions.barrierLow;     // top 1.93: hop it
const BAR = entityDefinitions.barrierBar;     // 1.61–2.39: hop or duck
const HIGH = entityDefinitions.barrierHigh;   // 1.67–5.44: duck

const lampHousing = (kit: Kit, x: number, y: number, z: number) => {
  kit.add(G.cyl(0.1, 0.11, 0.06, 12), "plastic", 0x1d1e24, { p: [x, y, z] });
};

/** A blinking lamp: a glowing dome + a sprite, its own object so it can blink alone. */
function lamp(name: string, color: number, x: number, y: number, z: number, r = 0.1): THREE.Object3D {
  const holder = new THREE.Group();
  holder.name = name;
  const dome = new THREE.Mesh(G.hemi(r, 12, 5), once(`mat:lampdome:${color}`, () => new THREE.MeshBasicMaterial({ color: hdr(color, 1.9), toneMapped: false })));
  const glow = glowSprite(color, r * 9, 0.7);
  glow.name = "lampGlow";
  glow.position.y = r * 0.4;
  holder.add(dome, glow);
  holder.position.set(x, y, z);
  return holder;
}

/** `buildBarrierLow()` — a yellow/black road barricade, top 1.93 m: hop it. */
export function buildBarrierLow(): THREE.Group {
  return instance("barrierLow", () => {
    const kit = new Kit();
    const w = LOW.width, top = LOW.yBase + LOW.height;
    const stripes = decalMaterial("barrier-low-stripes", stripesTex("#ffc21a", "#17181f", 7, 512, 64), { roughness: 0.4, cutout: true });
    for (const x of [-1, 1]) {
      kit.add(G.chamfer(0.36, 0.16, 1.05, 0.04), "rubber", 0x22232a, { p: [x * (w / 2 - 0.2), 0.08, 0] });
      kit.add(G.chamfer(0.14, top - 0.2, 0.14, 0.03), "paint", 0xe9ebf1, { p: [x * (w / 2 - 0.2), (top - 0.2) / 2 + 0.12, 0] });
      lampHousing(kit, x * (w / 2 - 0.2), top - 0.1, 0);
    }
    for (const y of [0.62, 1.12, 1.62]) {
      kit.add(G.chamfer(w, 0.36, 0.08, 0.025), "paint", 0xffc21a, { p: [0, y, 0] });
      kit.add(G.plane(w - 0.06, 0.3), stripes, 0xffffff, { p: [0, y, -0.045], r: FACE.front });
      kit.add(G.plane(w - 0.06, 0.3), stripes, 0xffffff, { p: [0, y, 0.045], r: FACE.back });
    }
    // little reflectors on the posts
    for (const x of [-1, 1]) kit.add(G.box(0.1, 0.18, 0.02), "glow", hdr(0xff3348, 1.2), { p: [x * (w / 2 - 0.2), 0.3, -0.08] });
    const root = new THREE.Group();
    root.name = "barrierLow";
    root.userData.anim = "blink";
    root.add(kit.build(), blobShadow(w + 0.8, 1.6));
    root.add(lamp("lamp0", 0xffa21f, -(w / 2 - 0.2), top - 0.07, 0), lamp("lamp1", 0xffa21f, w / 2 - 0.2, top - 0.07, 0));
    return root;
  });
}

/** `buildBarrierBar()` — a red/white boom at 1.61–2.39 m on two posts: hop or duck. */
export function buildBarrierBar(): THREE.Group {
  return instance("barrierBar", () => {
    const kit = new Kit();
    const w = BAR.width, y0 = BAR.yBase, h = BAR.height;
    const stripes = decalMaterial("barrier-bar-stripes", stripesTex("#e8323c", "#ffffff", 6, 512, 96), { roughness: 0.35, cutout: true });
    for (const x of [-1, 1]) {
      const px = x * (w / 2 - 0.12);
      kit.add(G.chamfer(0.56, 0.3, 0.7, 0.05), "matte", 0xb9b4ad, { p: [px, 0.15, 0] });
      kit.add(G.chamfer(0.2, y0 + h + 0.12, 0.2, 0.04), "paint", 0xf2f3f7, { p: [px, (y0 + h + 0.12) / 2 + 0.25, 0] });
      for (const y of [0.75, 1.25]) kit.add(G.box(0.21, 0.18, 0.21), "paint", 0xe8323c, { p: [px, y, 0] });
      lampHousing(kit, px, y0 + h + 0.4, 0);
    }
    kit.add(G.round(w, h, 0.26, 0.08, 2), "gloss", 0xffffff, { p: [0, y0 + h / 2, 0] });
    kit.add(G.plane(w - 0.14, h - 0.14), stripes, 0xffffff, { p: [0, y0 + h / 2, -0.132], r: FACE.front });
    kit.add(G.plane(w - 0.14, h - 0.14), stripes, 0xffffff, { p: [0, y0 + h / 2, 0.132], r: FACE.back });
    const root = new THREE.Group();
    root.name = "barrierBar";
    root.userData.anim = "blink";
    root.add(kit.build(), blobShadow(w + 0.8, 1.4));
    root.add(lamp("lamp0", 0xff2a3c, -(w / 2 - 0.12), y0 + h + 0.43, 0), lamp("lamp1", 0xff2a3c, w / 2 - 0.12, y0 + h + 0.43, 0));
    return root;
  });
}

const duckSign = (): THREE.Texture => canvasTex("sign-duck", 512, 512, (ctx, w, h) => {
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  roundRectPath(ctx, 14, 14, w - 28, h - 28, 34);
  ctx.fillStyle = "#e52b37";
  ctx.fill();
  roundRectPath(ctx, 34, 34, w - 68, h - 68, 22);
  ctx.lineWidth = 10;
  ctx.strokeStyle = "#ffffff";
  ctx.stroke();
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `900 132px ${DISPLAY_FONT}`;
  ctx.fillText("DUCK", w / 2, 150);
  // two big down chevrons
  for (const [y, s] of [[262, 1], [372, 1]] as [number, number][]) {
    ctx.beginPath();
    ctx.moveTo(w / 2 - 150 * s, y); ctx.lineTo(w / 2, y + 92 * s); ctx.lineTo(w / 2 + 150 * s, y);
    ctx.lineTo(w / 2 + 150 * s, y + 46 * s); ctx.lineTo(w / 2, y + 138 * s); ctx.lineTo(w / 2 - 150 * s, y + 46 * s);
    ctx.closePath();
    ctx.fill();
  }
});

/** `buildBarrierHigh()` — an overhead sign gantry, the panel 1.67–5.44 m: duck under it. */
export function buildBarrierHigh(): THREE.Group {
  return instance("barrierHigh", () => {
    const kit = new Kit();
    const y0 = HIGH.yBase, top = HIGH.yBase + HIGH.height, w = HIGH.width;
    const steel = 0xb7bdca;
    for (const x of [-1, 1]) {
      const px = x * (w / 2 + 0.06);
      kit.add(G.chamfer(0.5, 0.2, 0.5, 0.05), "satin", 0x8d93a1, { p: [px, 0.1, 0] });
      kit.add(G.chamfer(0.22, top + 0.1, 0.22, 0.04), "satin", steel, { p: [px, (top + 0.1) / 2 + 0.1, 0] });
    }
    kit.add(G.chamfer(w + 0.6, 0.24, 0.3, 0.05), "satin", steel, { p: [0, top + 0.1, 0] });
    // the sign: a red panel, white border and arrows (front), plain steel back
    const signH = top - 0.05 - (y0 + 0.36);
    const signY = y0 + 0.36 + signH / 2;
    kit.add(G.chamfer(w - 0.1, signH, 0.16, 0.04), "satin", 0x5d6372, { p: [0, signY, 0.02] });
    kit.add(G.plane(w - 0.2, signH - 0.1), printMaterial("sign-duck", duckSign(), { roughness: 0.35, emissive: 0.18 }), 0xffffff, { p: [0, signY, -0.065], r: FACE.front });
    // the clearance bar at its bottom edge: yellow/black, the lowest thing you must clear
    kit.add(G.chamfer(w, 0.3, 0.22, 0.04), "paint", 0xffc21a, { p: [0, y0 + 0.15, 0] });
    const stripes = decalMaterial("barrier-high-stripes", stripesTex("#ffc21a", "#17181f", 7, 512, 64), { roughness: 0.4, cutout: true });
    kit.add(G.plane(w - 0.06, 0.26), stripes, 0xffffff, { p: [0, y0 + 0.15, -0.115], r: FACE.front });
    kit.add(G.plane(w - 0.06, 0.26), stripes, 0xffffff, { p: [0, y0 + 0.15, 0.115], r: FACE.back });
    for (const x of [-1, 1]) lampHousing(kit, x * (w / 2 - 0.3), top + 0.25, -0.02);
    const root = new THREE.Group();
    root.name = "barrierHigh";
    root.userData.anim = "blink";
    root.add(kit.build(), blobShadow(w + 1.2, 1.4));
    root.add(lamp("lamp0", 0xffa21f, -(w / 2 - 0.3), top + 0.28, -0.02), lamp("lamp1", 0xffa21f, w / 2 - 0.3, top + 0.28, -0.02));
    return root;
  });
}
